import { waitUntil } from '@vercel/functions';
import { getJitteredWaitMs } from './backoff';

const IDLE_FLUSH_WAIT_MS = 5000;
const IDLE_FLUSH_JITTER_RATIO = 0.2;
const MAX_FLUSH_WAIT_MS = 60000;

export type FlushReason = 'idle_timeout' | 'max_timeout' | 'shutdown';

/**
 * Schedule helper that flushes when any of the following occur:
 * - the idle window elapses ({@link IDLE_FLUSH_WAIT_MS}, reset on every event), or
 * - the max window elapses ({@link MAX_FLUSH_WAIT_MS}, starts with the batch and is
 *   never reset, so a batch always flushes under continuous traffic).
 *
 * Flushing is purely time-based; batches are only cut down to size when the
 * ingest events are sent (see MAX_EVENTS_PER_REQUEST in ingest.ts).
 *
 * The scheduled flush awaits {@link onFlush} (including its ingest send + retries),
 * so the promise handed to `waitUntil` does not resolve until ingest completes.
 */
export class Scheduler {
  private resolveWait: ((reason: FlushReason) => void) | null = null;
  private pending: null | Promise<void> = null;
  private idleTimeout: null | ReturnType<typeof setTimeout> = null;
  private maxTimeout: null | ReturnType<typeof setTimeout> = null;

  constructor(
    private readonly onFlush: (reason: FlushReason) => void | Promise<void>,
  ) {}

  scheduleFlush(): void {
    if (!this.pending) {
      this.pending = (async () => {
        // wait for a timeout or the event count to reach the batch size
        const reason = await new Promise<FlushReason>((res) => {
          this.resolveWait = res;
        });

        // free state so new events start a fresh batch while ingest runs
        this.reset();

        // genuinely await ingest (incl. retries) so waitUntil covers the send
        await this.onFlush(reason);
      })();

      try {
        waitUntil(this.pending);
      } catch {
        // waitUntil is best-effort; falling through leaves a floating promise
      }

      // max window: starts with the batch and is never reset
      this.maxTimeout = setTimeout(
        () => this.resolveScheduledFlush('max_timeout'),
        MAX_FLUSH_WAIT_MS,
      );
    }

    // idle window: reset on every event
    this.resetIdleTimeout();
  }

  /**
   * Resolves any in-flight scheduled flush and waits for its ingest to finish.
   */
  async shutdown(): Promise<void> {
    this.clearTimeouts();
    const pending = this.pending;
    this.resolveScheduledFlush('shutdown');
    if (pending) await pending;
  }

  private resetIdleTimeout(): void {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(
      () => this.resolveScheduledFlush('idle_timeout'),
      getJitteredWaitMs(IDLE_FLUSH_WAIT_MS, IDLE_FLUSH_JITTER_RATIO),
    );
  }

  private resolveScheduledFlush(reason: FlushReason): void {
    this.clearTimeouts();
    this.resolveWait?.(reason);
  }

  private reset(): void {
    this.pending = null;
    this.resolveWait = null;
    this.clearTimeouts();
  }

  private clearTimeouts(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
    if (this.maxTimeout) {
      clearTimeout(this.maxTimeout);
      this.maxTimeout = null;
    }
  }
}
